import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo } from '@azure/msal-browser';
import { msalInstance, safeAcquireTokenSilent, ENTRA_ID_GROUPS, getUserGroups } from './authConfig';

// Types
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  authInitialized: boolean;
  hasGroupAccess: boolean;
  error: string | null;
  user: AccountInfo | null;
  userGroups: string[];
  userEKGroups: string[];
  userEKAreas: string[];
  accessToken: string | null;
  isAdmin: boolean;
  hasQuestionnairesGroup: boolean;
  hasSubmissionsGroup: boolean;
  hasSuppliersGroup: boolean;
  hasExportGroup: boolean;
  hasArticlesGroup: boolean;
  hasEKRole: boolean;
  logout: () => void;
  getUserEKCodes: () => string[];
  
  // Permission checks
  hasMenuPermission: (menu: string) => boolean;
  hasEKPermission: (ekCode: string) => boolean;
  canAccessSubmissions: boolean;
  canAccessQuestionnaires: boolean;
  canAccessSuppliers: boolean;
  canAccessArticles: boolean;
}

// Create Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { accounts, inProgress } = useMsal();
  
  // State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [hasGroupAccess, setHasGroupAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [userEKGroups, setUserEKGroups] = useState<string[]>([]);
  const [userEKAreas, setUserEKAreas] = useState<string[]>([]);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Derived state - NUR ENTRA ID GROUPS
  const isAdmin = userGroups.includes('supplierportal_admin');
  const hasQuestionnairesGroup = userGroups.includes('supplierportal_questionnaires');
  const hasSubmissionsGroup = userGroups.includes('supplierportal_submissions');
  const hasSuppliersGroup = userGroups.includes('supplierportal_suppliers');
  const hasExportGroup = userGroups.includes('supplierportal_export');
  const hasArticlesGroup = userGroups.includes('supplierportal_articles');
  const hasEKRole = userGroups.some(group => 
    group.startsWith('supplierportal_EK')
  );

  // Permission checks - NEUE BERECHTIGUNGSREGELN
  const hasMenuPermission = (menu: string): boolean => {
    if (isAdmin) return true;
    
    switch (menu) {
      case 'dashboard':
        return hasEKRole; // Dashboard: EK-Groups
      case 'questionnaires':
        return hasQuestionnairesGroup; // Questionnaires: supplierportal_questionnaires
      case 'submissions':
        return hasSubmissionsGroup && hasEKRole; // Submissions: supplierportal_submissions + EK-Group
      case 'suppliers':
        return hasSuppliersGroup; // Suppliers: supplierportal_suppliers
      case 'articles':
        return hasArticlesGroup; // Articles: supplierportal_articles (mit/ohne EK-Filter)
      case 'export':
        return hasExportGroup; // Export: supplierportal_export
      case 'analytics':
        return hasSubmissionsGroup; // Analytics: supplierportal_submissions (gleich wie Submissions)
      case 'workflows':
        return false; // Workflows: nur Admin
      case 'settings':
        return isAdmin;
      default:
        return false;
    }
  };

  const hasEKPermission = (ekCode: string): boolean => {
    if (isAdmin) return true;
    return userEKAreas.includes(ekCode);
  };

  const canAccessSubmissions = hasMenuPermission('submissions');
  const canAccessQuestionnaires = hasMenuPermission('questionnaires');
  const canAccessSuppliers = hasMenuPermission('suppliers');
  const canAccessArticles = hasMenuPermission('articles');

  // Get user EK codes - VEREINFACHT
  const getUserEKCodes = (): string[] => {
    if (isAdmin) {
      return Object.values(ENTRA_ID_GROUPS).filter(group => group.startsWith('supplierportal_EK'));
    }
    return userEKAreas;
  };

  // Logout function
  const logout = () => {
    console.log('[AUTH] Logging out...');
    
    // Check if there's already an interaction in progress ("none" means no interaction)
    if (inProgress !== "none") {
      console.log('[AUTH] Interaction already in progress, cannot logout now');
      return;
    }
    
    msalInstance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    }).catch((error) => {
      console.error('[AUTH] Logout failed:', error);
    });
  };

  // Initialize MSAL and check authentication
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log('[AUTH] Checking authentication state...');
        
        // Don't manually initialize MSAL - let MsalProvider handle it
        // Just check the current accounts
        const currentAccounts = msalInstance.getAllAccounts();
        console.log('[AUTH] Current accounts:', currentAccounts.length);

        if (currentAccounts.length > 0) {
          const account = currentAccounts[0];
          setUser(account);
          setIsAuthenticated(true);

          try {
            // Get access token
            const tokenResponse = await safeAcquireTokenSilent();
            setAccessToken(tokenResponse.accessToken);

            // Lade NUR Entra ID Groups - keine Token-Rollen mehr
            const azureGroups = await getUserGroups();
            
            // Mappe Entra ID Groups zu internen Rollen
            const appRoles: string[] = [];
            const ekRoles: string[] = [];
            const ekAreas: string[] = [];
            
            azureGroups.forEach((groupName: string) => {
              // Alle Entra ID Groups direkt verwenden
              appRoles.push(groupName);
              
              // EK Bereiche extrahieren
              if (groupName.startsWith('supplierportal_EK')) {
                const ekCode = groupName.replace('supplierportal_', '');
                ekRoles.push(ekCode);
                ekAreas.push(ekCode);
              }
            });
            
            setUserGroups(azureGroups); // Zeige die echten Gruppennamen
            setUserEKGroups(ekRoles);
            setUserEKAreas(ekAreas);

            // Check if user has any valid group
            const hasValidAccess = azureGroups.length > 0;
            setHasGroupAccess(hasValidAccess);

            // Debug: Vollständige Analyse
            console.log('[AUTH] Entra ID Groups Authentication:', {
              username: account.username,
              name: account.name,
              entraGroups: azureGroups,
              extractedAppRoles: appRoles,
              extractedEKRoles: ekRoles,
              hasGroupAccess: hasValidAccess
            });

          } catch (tokenError) {
            console.error('[AUTH] Token acquisition failed:', tokenError);
            setError('Token acquisition failed');
            setIsAuthenticated(false);
            setHasGroupAccess(false);
          }
        } else {
          console.log('[AUTH] No authenticated accounts found');
          setIsAuthenticated(false);
          setHasGroupAccess(false);
        }

        setAuthInitialized(true);
      } catch (initError) {
        console.error('[AUTH] Initialization failed:', initError);
        setError('Authentication initialization failed');
        setAuthInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    // Only initialize once when component mounts and MSAL is ready ("none" means no interaction)
    if (!authInitialized && inProgress === "none") {
      initializeAuth();
    }
  }, [authInitialized, inProgress]);

  // Watch for account changes
  useEffect(() => {
    if (authInitialized && inProgress === "none") {
      const currentAccounts = msalInstance.getAllAccounts();
      
      if (currentAccounts.length === 0 && isAuthenticated) {
        console.log('[AUTH] Account removed, logging out...');
        setIsAuthenticated(false);
        setHasGroupAccess(false);
        setUser(null);
        setUserGroups([]);
        setUserEKGroups([]);
        setUserEKAreas([]);
        setAccessToken(null);
      }
    }
  }, [accounts, authInitialized, inProgress, isAuthenticated]);

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    authInitialized,
    hasGroupAccess,
    error,
    user,
    userGroups,
    userEKGroups,
    userEKAreas,
    accessToken,
    isAdmin,
    hasQuestionnairesGroup,
    hasSubmissionsGroup,
    hasSuppliersGroup,
    hasExportGroup,
    hasArticlesGroup,
    hasEKRole,
    logout,
    getUserEKCodes,
    hasMenuPermission,
    hasEKPermission,
    canAccessSubmissions,
    canAccessQuestionnaires,
    canAccessSuppliers,
    canAccessArticles,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;